/* ==========================================================================
   BUNIVIA — assets/bunivia.js
   Conversion of the JavaScript inside pdp-full.html (block M · RENDER),
   plus the Shopify cart integration the prototype had no equivalent for.

   ARCHITECTURE
   Every behaviour is a custom element. Shopify's Theme Editor injects and
   removes section HTML at runtime, and custom elements initialise in
   connectedCallback and tear down in disconnectedCallback automatically.
   That makes duplicate event listeners structurally impossible — there is no
   re-init function to call twice, and no shopify:section:load hook to forget.
   Every document- and window-level listener is removed on disconnect.

   REMOVED FROM THE PROTOTYPE
   - the CONTENT object (all product data now comes from Liquid)
   - ph() placeholder SVG generator
   - state.cart, #cartCount and the fake cart increment
   - toast() and every fake Add to Cart / cart / chat confirmation
   - the demonstration nav array (the menu is now a real Shopify link list)
   - the newsletter's fake success branch — the real form is a Shopify
     customer form and success is reported server-side

   PRESERVED FROM THE PROTOTYPE
   - nearest() centre-distance maths, unchanged
   - rail arrow wrap-around: (i - 1 + n) % n and (i + 1) % n
   - mobile dropdown gating on window.innerWidth > 900
   - body scroll lock while the mobile nav or the size guide is open
   - independent accordion rows (more than one may be open at once)
   - footer columns as mobile accordions
   - feature-card carousel dot sync with resize re-check

   ONE KEYBOARD ADDITION
   Escape closes the mobile navigation. The prototype only bound Escape to the
   size-guide modal. Nothing visual changes; a full-screen menu with no
   keyboard exit is a genuine accessibility defect.

   CART INTEGRATION (bv-cart-count)
   Dawn's cart JS uses #cart-icon-bubble for two unrelated jobs:
     render target   cart-notification.js and cart-drawer.js both set
                     document.getElementById('cart-icon-bubble').innerHTML
                     after every add, and throw if the node is absent.
     drawer trigger  cart-drawer.js also binds click and SPACE on that same
                     node to open the drawer.
   sections/bunivia-header.liquid gives the id to a hidden, aria-hidden,
   unfocusable span so Dawn's markup never replaces the prototype's cart SVG.
   bv-cart-count then does the two jobs Dawn can no longer do for us:
     - mirrors the real item count out of the injected .cart-count-bubble
       into the visible Bunivia badge, in both cart modes
     - in drawer mode, wires the VISIBLE anchor to cartDrawer.open(anchor),
       with role="button", aria-haspopup="dialog", SPACE support and focus
       restoration. Enter needs no handler: the trigger is an <a href>, so
       Enter fires a native click that the click handler catches.
   If cart-drawer is missing or open() is not a function, nothing is
   prevented and the anchor simply navigates to /cart.

   MARKUP CONTRACT
     <bv-header>            [data-bv-burger] [data-bv-nav] .bv-nav__btn
     <bv-cart-count>        [data-bv-cart-mode] [data-bv-cart-trigger]
                            [data-bv-cart-count] #cart-icon-bubble
     <bv-gallery>           [data-bv-track] [data-bv-media] [data-bv-thumb]
                            [data-bv-prev] [data-bv-next] [data-bv-dots]
                            [data-bv-deferred] [data-bv-play]
                            <template data-bv-media-template>
     <bv-product-form>      [data-bv-variants] (JSON) [name="id"]
                            [data-bv-size] [data-bv-size-chosen]
                            [data-bv-price] [data-bv-compare]
                            [data-bv-atc] [data-bv-atc-text] [data-bv-size-error]
     <bv-accordion>         [data-bv-acc-btn] + [data-bv-acc-panel]
     <bv-footer-nav>        [data-bv-footer-toggle]
     <bv-size-guide>        [data-bv-close] [data-bv-tab] [data-bv-tabpanel]
                            opener: [data-bv-size-guide-open]
     <bv-carousel>          [data-bv-carousel-track] [data-bv-carousel-dots]
   ========================================================================== */

(function () {
  'use strict';

  var MOBILE_MAX = 900;

  function isMobile() {
    return window.innerWidth <= MOBILE_MAX;
  }

  /* prototype's nearest() — index of the child closest to the scroll centre */
  function nearestIndex(el) {
    var mid = el.scrollLeft + el.clientWidth / 2;
    var best = 0;
    var bd = Infinity;
    Array.prototype.forEach.call(el.children, function (child, i) {
      var d = Math.abs(child.offsetLeft + child.offsetWidth / 2 - mid);
      if (d < bd) {
        bd = d;
        best = i;
      }
    });
    return best;
  }

  function scrollToChild(track, child, behavior) {
    if (!child) return;
    var left = child.offsetLeft - (track.clientWidth - child.offsetWidth) / 2;
    if (typeof track.scrollTo === 'function') {
      track.scrollTo({ left: left, behavior: behavior || 'auto' });
    } else {
      track.scrollLeft = left;
    }
  }

  function pauseMediaIn(node) {
    if (!node) return;
    Array.prototype.forEach.call(node.querySelectorAll('video'), function (v) {
      try { v.pause(); } catch (e) { /* element not ready */ }
    });
    Array.prototype.forEach.call(node.querySelectorAll('iframe'), function (frame) {
      var src = frame.getAttribute('src') || '';
      var win = frame.contentWindow;
      if (!win) return;
      try {
        if (src.indexOf('youtube') > -1 || src.indexOf('youtu.be') > -1) {
          win.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } else if (src.indexOf('vimeo') > -1) {
          win.postMessage('{"method":"pause"}', '*');
        }
      } catch (e) { /* cross-origin frame not ready */ }
    });
  }

  function lockScroll(on) {
    document.body.style.overflow = on ? 'hidden' : '';
  }


  /* ========================================================================
     D · HEADER — burger, mobile submenus
     ======================================================================== */
  if (!customElements.get('bv-header')) {
    customElements.define('bv-header', class BvHeader extends HTMLElement {
      connectedCallback() {
        this.burger = this.querySelector('[data-bv-burger]');
        this.nav = this.querySelector('[data-bv-nav]');
        this.navButtons = Array.prototype.slice.call(this.querySelectorAll('.bv-nav__btn'));

        this.onBurger = this.handleBurger.bind(this);
        this.onNavClick = this.handleNavClick.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onResize = this.handleResize.bind(this);

        if (this.burger) this.burger.addEventListener('click', this.onBurger);
        this.navButtons.forEach(function (btn) {
          btn.addEventListener('click', this.onNavClick);
        }, this);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('resize', this.onResize);
      }

      disconnectedCallback() {
        if (this.burger) this.burger.removeEventListener('click', this.onBurger);
        (this.navButtons || []).forEach(function (btn) {
          btn.removeEventListener('click', this.onNavClick);
        }, this);
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('resize', this.onResize);
        lockScroll(false);
      }

      handleBurger(e) {
        if (!this.nav) return;
        var open = this.nav.classList.toggle('is-open');
        e.currentTarget.setAttribute('aria-expanded', String(open));
        lockScroll(open);
      }

      handleNavClick(e) {
        var btn = e.currentTarget;
        var dropdown = btn.nextElementSibling;
        if (window.innerWidth > MOBILE_MAX || !dropdown) return;
        e.preventDefault();
        var item = btn.parentElement;
        var open = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(open));
      }

      handleKeydown(e) {
        if (e.key !== 'Escape') return;
        if (!this.nav || !this.nav.classList.contains('is-open')) return;
        this.closeNav();
      }

      handleResize() {
        if (window.innerWidth > MOBILE_MAX) this.closeNav();
      }

      closeNav() {
        if (!this.nav) return;
        this.nav.classList.remove('is-open');
        if (this.burger) {
          this.burger.setAttribute('aria-expanded', 'false');
          this.burger.focus();
        }
        lockScroll(false);
      }
    });
  }


  /* ========================================================================
     CART — count mirroring, and the drawer trigger in drawer mode
     ======================================================================== */
  if (!customElements.get('bv-cart-count')) {
    customElements.define('bv-cart-count', class BvCartCount extends HTMLElement {
      connectedCallback() {
        this.mode = this.getAttribute('data-bv-cart-mode') || 'page';
        this.trigger = this.querySelector('[data-bv-cart-trigger]');
        this.badge = this.querySelector('[data-bv-cart-count]');
        this.source = this.querySelector('#cart-icon-bubble');

        if (this.source && window.MutationObserver) {
          this.observer = new MutationObserver(this.sync.bind(this));
          this.observer.observe(this.source, {
            childList: true,
            subtree: true,
            characterData: true
          });
        }

        if (this.mode === 'drawer' && this.trigger) {
          this.onClick = this.handleClick.bind(this);
          this.onKeydown = this.handleKeydown.bind(this);
          this.trigger.addEventListener('click', this.onClick);
          this.trigger.addEventListener('keydown', this.onKeydown);
          this.markAsDialogTrigger();
        }
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.trigger) {
          if (this.onClick) this.trigger.removeEventListener('click', this.onClick);
          if (this.onKeydown) this.trigger.removeEventListener('keydown', this.onKeydown);
        }
      }

      drawer() {
        return document.querySelector('cart-drawer');
      }

      /* Only advertise the button role once a usable drawer is on the page.
         cart-drawer.js is deferred, so retry briefly before giving up and
         leaving the anchor as a plain link to /cart. */
      markAsDialogTrigger(attempt) {
        var tries = attempt || 0;
        var drawer = this.drawer();
        if (drawer && typeof drawer.open === 'function') {
          this.trigger.setAttribute('role', 'button');
          this.trigger.setAttribute('aria-haspopup', 'dialog');
          return;
        }
        if (tries >= 20) return;
        var self = this;
        window.setTimeout(function () {
          if (self.isConnected) self.markAsDialogTrigger(tries + 1);
        }, 100);
      }

      openDrawer(e) {
        var drawer = this.drawer();
        if (!drawer || typeof drawer.open !== 'function') return false;
        e.preventDefault();
        drawer.open(this.trigger);
        return true;
      }

      handleClick(e) {
        this.openDrawer(e);
      }

      handleKeydown(e) {
        var code = (e.code || '').toUpperCase();
        if (code !== 'SPACE') return;
        this.openDrawer(e);
      }

      /* Reads the genuine item count out of the markup Dawn injected into the
         hidden node. Dawn omits the number entirely at 100 or more items. */
      sync() {
        if (!this.source || !this.badge) return;

        var bubble = this.source.querySelector('.cart-count-bubble');
        if (!bubble) {
          this.badge.textContent = '0';
          this.badge.hidden = true;
          return;
        }

        var visible = bubble.querySelector('[aria-hidden="true"]');
        var text = visible ? visible.textContent.trim() : '';
        if (!text) text = '99+';

        this.badge.textContent = text;
        this.badge.hidden = false;
      }
    });
  }


  /* ========================================================================
     E · GALLERY — rail, arrows, single track, dots, video pause
     ======================================================================== */
  if (!customElements.get('bv-gallery')) {
    customElements.define('bv-gallery', class BvGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-bv-track]');
        this.medias = Array.prototype.slice.call(this.querySelectorAll('[data-bv-media]'));
        this.thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-bv-thumb]'));
        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-bv-dots] > *'));
        this.prevBtn = this.querySelector('[data-bv-prev]');
        this.nextBtn = this.querySelector('[data-bv-next]');
        this.sectionId = this.getAttribute('data-section-id') || '';

        if (!this.medias.length) return;

        var start = parseInt(this.getAttribute('data-bv-active-index'), 10);
        this.index = isNaN(start) ? 0 : Math.max(0, Math.min(start, this.medias.length - 1));
        this.ticking = false;
        this.normalizing = false;
        this.settleTimer = null;

        this.buildLoop();

        this.onThumb = this.handleThumb.bind(this);
        this.onPrev = this.handlePrev.bind(this);
        this.onNext = this.handleNext.bind(this);
        this.onScroll = this.handleScroll.bind(this);
        this.onResize = this.handleResize.bind(this);
        this.onPlay = this.handlePlay.bind(this);
        this.onVariant = this.handleVariant.bind(this);

        this.thumbs.forEach(function (thumb) {
          thumb.addEventListener('click', this.onThumb);
        }, this);
        if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
        if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);
        if (this.track) this.track.addEventListener('scroll', this.onScroll, { passive: true });
        this.addEventListener('click', this.onPlay);
        window.addEventListener('resize', this.onResize);
        document.addEventListener('bv:variant:change', this.onVariant);

        this.apply(false);
        if (isMobile()) this.scrollToLogical(this.index, 'auto');
      }

      /* ── CIRCULAR MOBILE GALLERY ──────────────────────────────────────
         Prepends a clone of the LAST media and appends a clone of the FIRST,
         so logical slide 0 opens centred with real product media either side
         rather than an empty inset.

         Clones are deliberately inert:
           - data-bv-media, data-media-id and data-index are stripped, so
             this.medias, the dots, the logical index and variant-media
             mapping never see them
           - every id is removed, so no duplicate IDs enter the document
           - the play button and the <template> are removed and
             data-bv-deferred is dropped, so a video or model can never gain
             a second set of controls or a second media element
           - aria-hidden and tabindex -1 keep them out of the a11y tree
           - they keep .bv-gallery__media, so on desktop they inherit
             display: none exactly like any other inactive figure          */
      buildLoop() {
        if (!this.track || this.medias.length < 2) {
          if (this.track) this.track.setAttribute('data-bv-single', '');
          return;
        }

        var first = this.makeClone(this.medias[0]);
        var last = this.makeClone(this.medias[this.medias.length - 1]);
        this.track.insertBefore(last, this.track.firstChild);
        this.track.appendChild(first);
        this.track.setAttribute('data-bv-loop', '');
        this.hasLoop = true;
      }

      makeClone(node) {
        var c = node.cloneNode(true);
        c.removeAttribute('data-bv-media');
        c.removeAttribute('data-media-id');
        c.removeAttribute('data-index');
        c.classList.remove('is-active');
        c.setAttribute('data-bv-clone', '');
        c.setAttribute('aria-hidden', 'true');
        Array.prototype.forEach.call(c.querySelectorAll('[id]'), function (el) {
          el.removeAttribute('id');
        });
        Array.prototype.forEach.call(
          c.querySelectorAll('template, [data-bv-play], button'),
          function (el) { el.parentNode.removeChild(el); }
        );
        Array.prototype.forEach.call(
          c.querySelectorAll('[data-bv-deferred]'),
          function (el) {
            el.removeAttribute('data-bv-deferred');
            el.removeAttribute('data-loaded');
          }
        );
        Array.prototype.forEach.call(
          c.querySelectorAll('video, iframe, model-viewer'),
          function (el) { el.parentNode.removeChild(el); }
        );
        Array.prototype.forEach.call(c.querySelectorAll('[tabindex]'), function (el) {
          el.setAttribute('tabindex', '-1');
        });
        return c;
      }

      /* DOM child for a logical media index, allowing for the leading clone */
      childFor(i) {
        var offset = this.hasLoop ? 1 : 0;
        return this.track ? this.track.children[i + offset] : null;
      }

      /* logical index from a DOM child index, or -1 for a clone */
      logicalFrom(childIdx) {
        if (!this.hasLoop) return childIdx;
        if (childIdx === 0) return -1;
        if (childIdx === this.medias.length + 1) return this.medias.length;
        return childIdx - 1;
      }

      scrollToLogical(i, behavior) {
        var child = this.childFor(i);
        if (child) scrollToChild(this.track, child, behavior);
      }

      /* After momentum stops on a clone, jump silently to the real twin. */
      normalize() {
        if (!this.hasLoop || !this.track || this.normalizing) return;
        var childIdx = nearestIndex(this.track);
        var logical = this.logicalFrom(childIdx);
        if (logical !== -1 && logical !== this.medias.length) return;

        var target = logical === -1 ? this.medias.length - 1 : 0;
        this.normalizing = true;
        this.index = target;
        this.apply(false);
        this.scrollToLogical(target, 'auto');
        var self = this;
        window.requestAnimationFrame(function () {
          self.normalizing = false;
        });
      }

      disconnectedCallback() {
        (this.thumbs || []).forEach(function (thumb) {
          thumb.removeEventListener('click', this.onThumb);
        }, this);
        if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
        if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        this.removeEventListener('click', this.onPlay);
        window.removeEventListener('resize', this.onResize);
        document.removeEventListener('bv:variant:change', this.onVariant);
        window.clearTimeout(this.settleTimer);
        pauseMediaIn(this);
      }

      setIndex(i, scroll) {
        var n = this.medias.length;
        if (!n) return;
        var next = ((i % n) + n) % n;
        if (next === this.index && scroll !== true) {
          this.apply(false);
          return;
        }
        this.index = next;
        this.apply(scroll);
      }

      apply(scroll) {
        var self = this;

        this.medias.forEach(function (fig, i) {
          var active = i === self.index;
          fig.classList.toggle('is-active', active);
          if (!active) pauseMediaIn(fig);
        });

        this.thumbs.forEach(function (thumb, i) {
          thumb.setAttribute('aria-current', i === self.index ? 'true' : 'false');
        });

        this.dots.forEach(function (dot, i) {
          dot.classList.toggle('is-on', i === self.index);
        });

        if (scroll && isMobile() && this.track) {
          this.scrollToLogical(this.index, 'smooth');
        }
      }

      handleThumb(e) {
        var i = parseInt(e.currentTarget.getAttribute('data-bv-thumb'), 10);
        if (isNaN(i)) return;
        this.setIndex(i, isMobile());
      }

      handlePrev() {
        this.setIndex(this.index - 1, isMobile());
      }

      handleNext() {
        this.setIndex(this.index + 1, isMobile());
      }

      handleScroll() {
        if (!this.track || this.ticking || this.normalizing) return;
        this.ticking = true;
        var self = this;
        window.requestAnimationFrame(function () {
          self.ticking = false;
          if (!isMobile()) return;
          var logical = self.logicalFrom(nearestIndex(self.track));
          if (logical >= 0 && logical < self.medias.length && logical !== self.index) {
            self.index = logical;
            self.apply(false);
          }
        });

        window.clearTimeout(this.settleTimer);
        this.settleTimer = window.setTimeout(function () {
          if (isMobile()) self.normalize();
        }, 120);
      }

      handleResize() {
        this.apply(false);
        if (isMobile()) this.scrollToLogical(this.index, 'auto');
      }

      handlePlay(e) {
        var btn = e.target.closest ? e.target.closest('[data-bv-play]') : null;
        if (!btn || !this.contains(btn)) return;
        e.preventDefault();

        var wrapper = btn.closest('[data-bv-deferred]');
        if (!wrapper || wrapper.getAttribute('data-loaded') === 'true') return;

        var tpl = wrapper.querySelector('template[data-bv-media-template]');
        if (!tpl) return;

        wrapper.appendChild(tpl.content.cloneNode(true));
        wrapper.setAttribute('data-loaded', 'true');

        var video = wrapper.querySelector('video');
        if (video) {
          video.setAttribute('controls', 'controls');
          var playing = video.play();
          if (playing && typeof playing.catch === 'function') {
            playing.catch(function () { /* autoplay blocked, controls are shown */ });
          }
        }

        var fig = wrapper.closest('[data-bv-media]');
        if (fig) {
          var i = this.medias.indexOf(fig);
          if (i > -1 && i !== this.index) this.setIndex(i, isMobile());
        }
      }

      handleVariant(e) {
        var detail = e.detail || {};
        if (detail.sectionId && this.sectionId && detail.sectionId !== this.sectionId) return;
        if (!detail.mediaId) return;
        for (var i = 0; i < this.medias.length; i++) {
          if (this.medias[i].getAttribute('data-media-id') === String(detail.mediaId)) {
            this.setIndex(i, isMobile());
            return;
          }
        }
      }
    });
  }


  /* ========================================================================
     DESKTOP GALLERY RELEASE

     The gallery stays position: sticky throughout — nothing toggles it to
     relative, so there is no normal-flow snap-back to compensate for. The
     only thing written is a vertical transform, a paint operation that cannot
     alter width, height, left, grid tracks or layout of any kind. No CSS file
     is involved.

     RELEASE LINE
     .bv-halfsize reaching the live bottom edge of the sticky .bv-header:

       releaseDistance = max(0, headerBottom - halfSizeTop)

     Zero while Half Size sits below the header, then one pixel per pixel as
     it travels beneath it. Read live from getBoundingClientRect() rather than
     from --bv-header-h, so it stays correct if the announcement bar or header
     height changes. Reversing the scroll shrinks the same value back to zero.

     WHY NOT SIMPLY -releaseDistance
     .bv-halfsize is in the right column and keeps scrolling regardless of the
     gallery, so releaseDistance keeps growing. Once .bv-pdp's bottom edge
     reaches the pinned gallery, native sticky containment ALSO pushes it up.
     Applying -releaseDistance on top gives roughly two pixels of gallery
     travel per pixel of scroll.

     So the translation is computed against the gallery's own untransformed
     position:

       translateY = (stickyTop - releaseDistance) - baseTop

     While pinned, baseTop equals stickyTop and this reduces to
     -releaseDistance. Once containment engages, baseTop drops below stickyTop
     on its own and the expression shrinks by exactly that amount, so the
     transform absorbs the native movement rather than adding to it. The
     visible top always lands on stickyTop - releaseDistance and the rate
     stays 1:1 across the handover, with no jump.

     MEASURING baseTop
     getBoundingClientRect() INCLUDES transforms, so the transform is cleared
     before the read. Without that, each frame would feed its own translation
     back into the next and compound. Clear, measure, compute, apply — all in
     one requestAnimationFrame, one read then one write, no layout thrash.

     No transition, easing, keyframes, opacity or scale. No fixed positioning,
     no spacer, no extra visible element, no resizing. Below 901px the
     transform is cleared and the element does nothing.
     ======================================================================== */
  if (!customElements.get('bv-gallery-release')) {
    customElements.define('bv-gallery-release', class BvGalleryRelease extends HTMLElement {
      connectedCallback() {
        this.pdp = this.closest('.bv-pdp');
        if (!this.pdp) return;

        this.gallery = this.pdp.querySelector('.bv-gallery');
        this.halfSize = this.pdp.querySelector('.bv-halfsize');
        this.header = document.querySelector('.bv-header');
        if (!this.gallery || !this.halfSize || !this.header) return;

        this.frame = null;
        this.schedule = this.schedule.bind(this);

        window.addEventListener('scroll', this.schedule, { passive: true });
        window.addEventListener('resize', this.schedule);

        this.schedule();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.schedule);
        window.removeEventListener('resize', this.schedule);
        if (this.frame) window.cancelAnimationFrame(this.frame);
        if (this.gallery) this.gallery.style.transform = '';
      }

      schedule() {
        if (this.frame) return;
        var self = this;
        this.frame = window.requestAnimationFrame(function () {
          self.frame = null;
          self.update();
        });
      }

      update() {
        if (!this.gallery) return;

        if (isMobile()) {
          if (this.gallery.style.transform) this.gallery.style.transform = '';
          return;
        }

        /* Clear first: getBoundingClientRect includes transforms, so this is
           the only way to read the true sticky/layout position. */
        this.gallery.style.transform = '';

        var baseTop = this.gallery.getBoundingClientRect().top;
        var stickyTop = parseFloat(window.getComputedStyle(this.gallery).top);
        if (isNaN(stickyTop)) stickyTop = baseTop;

        var headerBottom = this.header.getBoundingClientRect().bottom;
        var halfSizeTop = this.halfSize.getBoundingClientRect().top;
        var releaseDistance = Math.max(0, headerBottom - halfSizeTop);

        if (releaseDistance === 0) return;

        var targetVisualTop = stickyTop - releaseDistance;
        var translateY = targetVisualTop - baseTop;

        this.gallery.style.transform = 'translateY(' + translateY + 'px)';
      }
    });
  }


  /* ========================================================================
     E · PRODUCT FORM — size selection, variant resolution, price, ATC state
     ======================================================================== */
  if (!customElements.get('bv-product-form')) {
    customElements.define('bv-product-form', class BvProductForm extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.getAttribute('data-section-id') || '';
        this.productUrl = this.getAttribute('data-bv-url') || window.location.pathname;
        this.sizeIndex = parseInt(this.getAttribute('data-bv-size-index'), 10);
        if (isNaN(this.sizeIndex)) this.sizeIndex = 0;
        this.unitPrefix = this.getAttribute('data-bv-unit') || '';
        this.atcLabel = this.getAttribute('data-bv-atc-label') || 'Add To Cart';
        this.soldOutLabel = this.getAttribute('data-bv-soldout-label') || 'Sold out';
        this.unavailableLabel = this.getAttribute('data-bv-unavailable-label') || 'Unavailable';

        this.variants = this.readVariants();
        this.sizeButtons = Array.prototype.slice.call(this.querySelectorAll('[data-bv-size]'));
        this.idInput = this.querySelector('input[name="id"]');
        this.chosenNode = this.querySelector('[data-bv-size-chosen]');
        this.priceNode = this.querySelector('[data-bv-price]');
        this.compareNode = this.querySelector('[data-bv-compare]');
        this.saveNode = this.querySelector('[data-bv-save]');
        this.atcButton = this.querySelector('[data-bv-atc]');
        this.atcText = this.querySelector('[data-bv-atc-text]');
        this.errorNode = this.querySelector('[data-bv-size-error]');

        this.sizesWrap = this.querySelector('[data-bv-sizes]');

        this.selectedSize = this.getAttribute('data-bv-initial-size') || null;

        /* Mobile summary relocation. The reference screenshots put the
           title, reviews, colour and price above the gallery on mobile. The
           node is MOVED, never cloned, and stays inside this element so
           this.priceNode above keeps resolving. */
        this.headNode = this.querySelector('[data-bv-head]');
        this.headAnchor = this.querySelector('[data-bv-head-anchor]');
        this.summarySlot = this.querySelector('[data-bv-mobile-summary-slot]');
        this.mq = window.matchMedia('(max-width: ' + MOBILE_MAX + 'px)');
        this.onBreakpoint = this.placeSummary.bind(this);
        if (this.mq.addEventListener) {
          this.mq.addEventListener('change', this.onBreakpoint);
        } else if (this.mq.addListener) {
          this.mq.addListener(this.onBreakpoint);
        }
        this.placeSummary();

        this.onSize = this.handleSize.bind(this);
        this.onSubmitCapture = this.handleSubmitCapture.bind(this);

        this.sizeButtons.forEach(function (btn) {
          btn.addEventListener('click', this.onSize);
        }, this);

        /* Capture on this ancestor fires before <product-form>'s own submit
           handler on the inner <form>, so the size guard can stop it. */
        this.addEventListener('submit', this.onSubmitCapture, true);

        this.paint();
      }

      disconnectedCallback() {
        (this.sizeButtons || []).forEach(function (btn) {
          btn.removeEventListener('click', this.onSize);
        }, this);
        this.removeEventListener('submit', this.onSubmitCapture, true);
        if (this.mq && this.onBreakpoint) {
          if (this.mq.removeEventListener) {
            this.mq.removeEventListener('change', this.onBreakpoint);
          } else if (this.mq.removeListener) {
            this.mq.removeListener(this.onBreakpoint);
          }
        }
      }

      /* Below 900px the summary sits in the slot, which CSS orders above the
         gallery. Above 900px it returns immediately after its anchor, its
         original position inside .bv-buybox. Idempotent: if the node is
         already where it belongs, nothing is touched. Exits safely if any
         node is missing. */
      placeSummary() {
        if (!this.headNode || !this.headAnchor || !this.summarySlot) return;
        if (this.mq.matches) {
          if (this.headNode.parentNode !== this.summarySlot) {
            this.summarySlot.appendChild(this.headNode);
          }
        } else if (this.headNode.previousElementSibling !== this.headAnchor) {
          this.headAnchor.insertAdjacentElement('afterend', this.headNode);
        }
      }

      readVariants() {
        var node = this.querySelector('[data-bv-variants]');
        if (!node) return [];
        try {
          var parsed = JSON.parse(node.textContent);
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      }

      pickPreferred(list) {
        for (var i = 0; i < list.length; i++) {
          if (list[i].available) return list[i];
        }
        return list.length ? list[0] : null;
      }

      matchOn(list, index, value) {
        if (index < 0 || value === null || value === undefined) return [];
        var out = [];
        for (var i = 0; i < list.length; i++) {
          var opts = list[i].options || [];
          if (String(opts[index]) === String(value)) out.push(list[i]);
        }
        return out;
      }

      /* Colours are separate products now, so size is the only axis this
         picker resolves. Each colour product has its own variants. */
      currentVariant() {
        if (!this.variants.length) return null;
        if (!this.sizeButtons.length) return this.variants[0];
        if (!this.selectedSize) return null;
        var sizeMatches = this.matchOn(this.variants, this.sizeIndex, this.selectedSize);
        if (!sizeMatches.length) return null;
        return this.pickPreferred(sizeMatches);
      }

      handleSize(e) {
        var btn = e.currentTarget;
        if (btn.hasAttribute('disabled')) return;
        this.selectedSize = btn.getAttribute('data-bv-size');
        this.hideError();
        this.paint();
        this.pushUrl();
        this.announceMedia();
      }

      paint() {
        var self = this;
        var variant = this.currentVariant();

        this.sizeButtons.forEach(function (btn) {
          var isOn = self.selectedSize !== null &&
                     btn.getAttribute('data-bv-size') === self.selectedSize;
          btn.setAttribute('aria-pressed', isOn ? 'true' : 'false');
        });

        if (this.chosenNode) {
          this.chosenNode.textContent = this.selectedSize
            ? (this.unitPrefix ? this.unitPrefix + ' ' + this.selectedSize : this.selectedSize)
            : '';
        }

        if (variant) {
          if (this.idInput) {
            this.idInput.value = variant.id;
            this.idInput.disabled = !variant.available;
          }
          if (this.priceNode) this.priceNode.innerHTML = variant.price;

          /* Selected Color option, straight off the resolved variant. Never a
             literal — if the product has no colour option the index is -1 and
             the server-rendered text is left exactly as Liquid printed it. */
          if (this.compareNode) {
            if (variant.compare) {
              this.compareNode.innerHTML = variant.compare;
              this.compareNode.hidden = false;
            } else {
              this.compareNode.innerHTML = '';
              this.compareNode.hidden = true;
            }
          }

          /* SAVE badge. variant.save is the per-variant saving computed and
             money-formatted server-side in the section's variants JSON, so
             no client-side currency parsing happens here. Mirrors the
             compare-at pattern above: absent saving hides the node, and the
             desktop CSS never shows it regardless. */
          if (this.saveNode) {
            if (variant.save) {
              this.saveNode.textContent = 'SAVE ' + this.stripTags(variant.save);
              this.saveNode.hidden = false;
            } else {
              this.saveNode.textContent = '';
              this.saveNode.hidden = true;
            }
          }
        }

        if (this.atcButton && this.atcText) {
          if (variant && !variant.available) {
            this.atcButton.setAttribute('aria-disabled', 'true');
            this.atcText.textContent = this.soldOutLabel;
          } else if (variant) {
            this.atcButton.removeAttribute('aria-disabled');
            this.atcText.textContent = this.atcLabel + ' - ' + this.stripTags(variant.price);
          } else {
            this.atcButton.removeAttribute('aria-disabled');
            var base = this.getAttribute('data-bv-base-price') || '';
            this.atcText.textContent = base ? this.atcLabel + ' - ' + base : this.atcLabel;
          }
        }
      }

      stripTags(html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html || '';
        return (tmp.textContent || '').trim();
      }

      pushUrl() {
        var variant = this.currentVariant();
        if (!variant || !window.history || !window.history.replaceState) return;
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) { /* older browser, URL untouched */ }
      }

      announceMedia() {
        var variant = this.currentVariant();
        if (!variant || !variant.media_id) return;
        document.dispatchEvent(new CustomEvent('bv:variant:change', {
          detail: { sectionId: this.sectionId, mediaId: variant.media_id }
        }));
      }

      showError() {
        if (this.errorNode) this.errorNode.classList.add('is-shown');
        if (this.sizesWrap && this.sizesWrap.scrollIntoView) {
          this.sizesWrap.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }

      hideError() {
        if (this.errorNode) this.errorNode.classList.remove('is-shown');
      }

      handleSubmitCapture(e) {
        if (!this.sizeButtons.length) return;
        if (this.selectedSize && this.currentVariant()) return;
        e.preventDefault();
        e.stopPropagation();
        this.showError();
      }
    });
  }


  /* ========================================================================
     E · ACCORDIONS — product information rows
     ======================================================================== */
  if (!customElements.get('bv-accordion')) {
    customElements.define('bv-accordion', class BvAccordion extends HTMLElement {
      connectedCallback() {
        this.buttons = Array.prototype.slice.call(this.querySelectorAll('[data-bv-acc-btn]'));
        this.onToggle = this.handleToggle.bind(this);
        this.buttons.forEach(function (btn) {
          btn.addEventListener('click', this.onToggle);
        }, this);
      }

      disconnectedCallback() {
        (this.buttons || []).forEach(function (btn) {
          btn.removeEventListener('click', this.onToggle);
        }, this);
      }

      handleToggle(e) {
        var btn = e.currentTarget;
        var panel = btn.nextElementSibling;
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        if (panel) panel.classList.toggle('is-open', !open);
      }
    });
  }


  /* ========================================================================
     K · FOOTER — columns become accordions below 900px
     ======================================================================== */
  if (!customElements.get('bv-footer-nav')) {
    customElements.define('bv-footer-nav', class BvFooterNav extends HTMLElement {
      connectedCallback() {
        this.toggles = Array.prototype.slice.call(this.querySelectorAll('[data-bv-footer-toggle]'));
        this.onToggle = this.handleToggle.bind(this);
        this.toggles.forEach(function (btn) {
          btn.addEventListener('click', this.onToggle);
        }, this);
      }

      disconnectedCallback() {
        (this.toggles || []).forEach(function (btn) {
          btn.removeEventListener('click', this.onToggle);
        }, this);
      }

      handleToggle(e) {
        var btn = e.currentTarget;
        var col = btn.parentElement;
        if (!col) return;
        var open = col.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', String(open));
      }
    });
  }


  /* ========================================================================
     SIZE GUIDE MODAL — open, close, backdrop, Escape, focus trap, tabs
     ======================================================================== */
  if (!customElements.get('bv-size-guide')) {
    customElements.define('bv-size-guide', class BvSizeGuide extends HTMLElement {
      connectedCallback() {
        this.modal = this.querySelector('[data-bv-modal]');
        this.card = this.querySelector('[data-bv-modal-card]');
        this.closers = Array.prototype.slice.call(this.querySelectorAll('[data-bv-close]'));
        this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-bv-tab]'));
        this.panels = Array.prototype.slice.call(this.querySelectorAll('[data-bv-tabpanel]'));
        this.lastFocused = null;

        var scope = this.closest('[data-bv-section]') || document;
        this.openers = Array.prototype.slice.call(scope.querySelectorAll('[data-bv-size-guide-open]'));

        this.onOpen = this.handleOpen.bind(this);
        this.onClose = this.handleClose.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onTab = this.handleTab.bind(this);
        this.onTabKeys = this.handleTabKeys.bind(this);

        this.openers.forEach(function (btn) {
          btn.addEventListener('click', this.onOpen);
        }, this);
        this.closers.forEach(function (btn) {
          btn.addEventListener('click', this.onClose);
        }, this);
        this.tabs.forEach(function (btn) {
          btn.addEventListener('click', this.onTab);
          btn.addEventListener('keydown', this.onTabKeys);
        }, this);
        document.addEventListener('keydown', this.onKeydown);
      }

      disconnectedCallback() {
        (this.openers || []).forEach(function (btn) {
          btn.removeEventListener('click', this.onOpen);
        }, this);
        (this.closers || []).forEach(function (btn) {
          btn.removeEventListener('click', this.onClose);
        }, this);
        (this.tabs || []).forEach(function (btn) {
          btn.removeEventListener('click', this.onTab);
          btn.removeEventListener('keydown', this.onTabKeys);
        }, this);
        document.removeEventListener('keydown', this.onKeydown);
        lockScroll(false);
      }

      isOpen() {
        return !!this.modal && this.modal.classList.contains('is-open');
      }

      handleOpen(e) {
        if (!this.modal) return;
        e.preventDefault();
        this.lastFocused = document.activeElement;
        this.modal.classList.add('is-open');
        lockScroll(true);
        var focusTarget = this.querySelector('[data-bv-close]:not([data-bv-backdrop])') ||
                          this.card;
        if (focusTarget && focusTarget.focus) focusTarget.focus();
      }

      handleClose(e) {
        if (e) e.preventDefault();
        this.close();
      }

      close() {
        if (!this.modal) return;
        this.modal.classList.remove('is-open');
        lockScroll(false);
        if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();
      }

      handleKeydown(e) {
        if (!this.isOpen()) return;
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close();
          return;
        }
        if (e.key === 'Tab') this.trapFocus(e);
      }

      trapFocus(e) {
        if (!this.card) return;
        var focusables = this.card.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        var list = Array.prototype.filter.call(focusables, function (el) {
          return el.offsetWidth > 0 || el.offsetHeight > 0 || el === document.activeElement;
        });
        if (!list.length) return;
        var first = list[0];
        var last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      handleTab(e) {
        this.selectTab(e.currentTarget);
      }

      handleTabKeys(e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        var i = this.tabs.indexOf(e.currentTarget);
        if (i < 0) return;
        var n = this.tabs.length;
        var next = e.key === 'ArrowRight' ? (i + 1) % n : (i - 1 + n) % n;
        this.selectTab(this.tabs[next]);
        this.tabs[next].focus();
      }

      selectTab(tab) {
        if (!tab) return;
        var name = tab.getAttribute('data-bv-tab');
        this.tabs.forEach(function (t) {
          var on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
        });
        this.panels.forEach(function (panel) {
          panel.classList.toggle('is-on', panel.getAttribute('data-bv-tabpanel') === name);
        });
      }
    });
  }


  /* ========================================================================
     H · FEATURE-CARD CAROUSEL — mobile dot sync
     ======================================================================== */
  if (!customElements.get('bv-carousel')) {
    customElements.define('bv-carousel', class BvCarousel extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-bv-carousel-track]');
        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-bv-carousel-dots] > *'));
        if (!this.track) return;

        this.index = 0;
        this.ticking = false;

        this.onScroll = this.handleScroll.bind(this);
        this.onResize = this.handleResize.bind(this);

        this.track.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize);

        this.paint();
      }

      disconnectedCallback() {
        if (this.track) this.track.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onResize);
      }

      paint() {
        var self = this;
        this.dots.forEach(function (dot, i) {
          dot.classList.toggle('is-on', i === self.index);
        });
      }

      handleScroll() {
        if (this.ticking) return;
        this.ticking = true;
        var self = this;
        window.requestAnimationFrame(function () {
          self.ticking = false;
          var i = nearestIndex(self.track);
          if (i !== self.index) {
            self.index = i;
            self.paint();
          }
        });
      }

      handleResize() {
        this.index = nearestIndex(this.track);
        this.paint();
      }
    });
  }

  /* Mount host. The section markup has no <bv-gallery-release> element and no
     CSS file is touched by this change, so one is created at runtime with the
     hidden attribute set BEFORE it is appended. hidden keeps it out of the
     grid as a visible item while still allowing connectedCallback to run. */
  function mountGalleryRelease() {
    var list = document.querySelectorAll('.bv-pdp');
    Array.prototype.forEach.call(list, function (pdp) {
      if (pdp.querySelector('bv-gallery-release')) return;
      var el = document.createElement('bv-gallery-release');
      el.hidden = true;
      el.setAttribute('aria-hidden', 'true');
      pdp.appendChild(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountGalleryRelease);
  } else {
    mountGalleryRelease();
  }
  document.addEventListener('shopify:section:load', mountGalleryRelease);
})();
